import { Link } from 'react-router-dom'
import { CONTACT_EMAIL } from '../../../config'

type FooterLink = { label: string; to?: string; href?: string; onClick?: () => void }

function makeColumn(scrollTo?: (id: string) => void) {
  return {
    product: [
      { label: 'How it works', onClick: () => scrollTo?.('how-it-works') },
      { label: 'Who it’s for', onClick: () => scrollTo?.('who-its-for') },
      { label: 'Pricing', onClick: () => scrollTo?.('pricing') },
    ] as FooterLink[],
    company: [
      { label: 'Get started', to: '/signup' },
      { label: 'Sign in', to: '/signin' },
      { label: 'Contact', href: `mailto:${CONTACT_EMAIL}` },
    ] as FooterLink[],
    legal: [
      { label: 'Privacy & Terms', href: '/privacy' },
    ] as FooterLink[],
  }
}

interface FooterSectionProps {
  onScrollTo?: (id: string) => void
}

export default function FooterSection({ onScrollTo }: FooterSectionProps = {}) {
  const cols = makeColumn(onScrollTo)

  function renderLink(link: FooterLink) {
    const base = {
      color: '#FBE6D4',
      textDecoration: 'none',
      transition: 'color 150ms ease',
      fontWeight: 600,
    } as const

    if (link.to) {
      return (
        <Link to={link.to} className="footer-link text-sm" style={base}>
          {link.label}
        </Link>
      )
    }
    if (link.href) {
      return (
        <a href={link.href} className="footer-link text-sm" style={base}>
          {link.label}
        </a>
      )
    }
    return (
      <button
        type="button"
        onClick={link.onClick}
        className="footer-link text-sm text-left"
        style={{ ...base, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        {link.label}
      </button>
    )
  }

  return (
    <footer
      className="px-6 pt-20 pb-8 relative overflow-hidden"
      style={{ backgroundColor: '#111', color: '#FBE6D4' }}
    >
      <div className="max-w-6xl mx-auto relative">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/logo.svg" alt="" className="w-9 h-9" aria-hidden />
              <span
                className="font-extrabold text-2xl tracking-tight"
                style={{ color: '#FBE6D4', letterSpacing: '-0.02em' }}
              >
                Nudge
              </span>
            </div>
            <p
              className="text-sm max-w-[260px]"
              style={{ color: 'rgba(251,230,212,0.7)', lineHeight: 1.55 }}
            >
              Bring back the customers you already earned — quietly, automatically, in your voice.
            </p>
          </div>

          {/* Product */}
          <div>
            <h4
              className="text-xs font-bold uppercase mb-4 font-dm-mono"
              style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}
            >
              Product
            </h4>
            <ul className="flex flex-col gap-2.5">
              {cols.product.map((l) => (
                <li key={l.label}>{renderLink(l)}</li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4
              className="text-xs font-bold uppercase mb-4 font-dm-mono"
              style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}
            >
              Company
            </h4>
            <ul className="flex flex-col gap-2.5">
              {cols.company.map((l) => (
                <li key={l.label}>{renderLink(l)}</li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4
              className="text-xs font-bold uppercase mb-4 font-dm-mono"
              style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}
            >
              Legal
            </h4>
            <ul className="flex flex-col gap-2.5">
              {cols.legal.map((l) => (
                <li key={l.label}>{renderLink(l)}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="mt-16 pt-6 flex flex-col-reverse sm:flex-row items-center justify-between gap-4"
          style={{ borderTop: '1px solid rgba(251,230,212,0.15)' }}
        >
          <p className="text-xs" style={{ color: 'rgba(251,230,212,0.5)' }}>
            © {new Date().getFullYear()} Nudge. Built for small businesses everywhere.
          </p>
          <div className="flex items-center gap-3">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="footer-icon inline-flex items-center justify-center"
              aria-label="Email Nudge"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                border: '1.5px solid rgba(251,230,212,0.3)',
                color: '#FBE6D4',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              </svg>
            </a>
            <a
              href="https://twitter.com"
              target="_blank"
              rel="noreferrer"
              className="footer-icon inline-flex items-center justify-center"
              aria-label="Nudge on X"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                border: '1.5px solid rgba(251,230,212,0.3)',
                color: '#FBE6D4',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.965 6.817H1.68l7.73-8.835L1.254 2.25h6.83l4.713 6.231 5.447-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
              </svg>
            </a>
            <a
              href="https://github.com"
              target="_blank"
              rel="noreferrer"
              className="footer-icon inline-flex items-center justify-center"
              aria-label="Nudge on GitHub"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                border: '1.5px solid rgba(251,230,212,0.3)',
                color: '#FBE6D4',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.1.79-.25.79-.55v-2.16c-3.2.7-3.88-1.37-3.88-1.37-.53-1.34-1.29-1.7-1.29-1.7-1.05-.72.08-.7.08-.7 1.16.08 1.78 1.19 1.78 1.19 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.55-.29-5.23-1.28-5.23-5.7 0-1.26.45-2.29 1.18-3.1-.12-.29-.51-1.47.11-3.06 0 0 .97-.31 3.18 1.18a11.1 11.1 0 0 1 5.8 0c2.21-1.49 3.18-1.18 3.18-1.18.62 1.59.23 2.77.11 3.06.73.81 1.18 1.84 1.18 3.1 0 4.43-2.69 5.4-5.25 5.69.41.36.78 1.06.78 2.14v3.18c0 .31.21.66.8.55C20.21 21.39 23.5 17.08 23.5 12c0-6.35-5.15-11.5-11.5-11.5z" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .footer-link:hover { color: var(--accent) !important; }
        .footer-icon { transition: color 150ms ease, border-color 150ms ease, background-color 150ms ease; }
        .footer-icon:hover {
          color: #111 !important;
          background-color: var(--accent);
          border-color: var(--accent) !important;
        }
      `}</style>
    </footer>
  )
}
